import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Slide,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2/Grid2"
import {
    ReactElement,
    RefObject,
    SyntheticEvent,
    useRef,
    useState,
} from "react"
import { useNavigate } from "react-router-dom"
import { TypeAnimation } from "react-type-animation"

import HOMEPAGE_FAQS from "./publicHomepageFaqs"
import Evcp2 from "../../assets/images/evcp2.png"
import Hp from "../../assets/images/hp1.png"
import SolarPanel1 from "../../assets/images/solarPanel1.png"
import { ReactComponent as CarSvg } from "../../assets/kpi-three.svg"
import AppButton from "../../components/buttons/appButton"
import CheckMarkList from "../../components/dataDisplay/lsits/checkMarkList"
import { SLIDE_IN_TIMEOUT } from "../../constants/generalConstants"
import { useScreenSize } from "../../hooks/useScreenSize"
import { LIGHT } from "../../redux/preferencesSlice"
import { LOGIN } from "../../router/routes"
import { getContentFramePadding } from "../../webpageFrame/contentFrames"
import { AppTab } from "../adminPortal/adminPortal"

enum InstallerTabValue {
    LARGE_INSTALLERS = "large_installers",
    SOFTWARE_PROVIDERS = "software_providers",
}

const PADDING_LEFT = {
    xs: 2,
    md: 7,
}

const MARGIN_SECTION_TITLE_CONTENT = 8
const SECTION_PY = { xs: 10, md: 13 }
const SUBTITLE_MARGIN = 1

const PublicHomepage = (): ReactElement => {
    const theme = useTheme()
    const navigate = useNavigate()
    const { isMob } = useScreenSize()
    const [tabValue, setTabValue] = useState(InstallerTabValue.LARGE_INSTALLERS)

    const aboutRef = useRef<HTMLElement | null>(null)
    const manuRef = useRef<HTMLElement | null>(null)
    const customerRef = useRef<HTMLElement | null>(null)

    const handleChange = (
        _event: SyntheticEvent,
        newTabSelection: InstallerTabValue
    ): void => {
        setTabValue(newTabSelection)
    }

    const navToLogin = (): void => {
        navigate(LOGIN)
    }

    const scrollToSection = (ref: RefObject<HTMLElement>): void => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth" })
        }
    }

    return (
        <>
            <Grid
                container
                spacing={{
                    xs: 4,
                    lg: 8,
                }}
                padding={getContentFramePadding(isMob)}
            >
                <Grid
                    lg={6}
                    md={11}
                    xs={12}
                    paddingLeft={PADDING_LEFT}
                    alignContent="center"
                >
                    <Stack
                        spacing={{
                            xs: 3,
                            lg: 6,
                        }}
                        marginTop={{
                            xs: 4,
                            md: 12,
                            lg: 15,
                        }}
                        marginBottom={{
                            xs: 6,
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h2"
                                color="text.primary"
                                marginBottom={SUBTITLE_MARGIN}
                            >
                                An easier way to manage domestic connections
                            </Typography>
                            <Typography />
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                component="div"
                                fontWeight="400"
                                style={{ lineHeight: "1.3" }}
                            >
                                Welcome to{" "}
                                <Box
                                    color={theme.palette.primary.main}
                                    display="inline"
                                    fontWeight="500"
                                >
                                    ENA Connect Direct
                                </Box>
                                , a new, faster way to get approval to connect
                                residential low-carbon technology to the grid
                            </Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                            <Typography
                                color="text.secondary"
                                component="div"
                                fontSize={{
                                    xs: "1.5rem",
                                    sm: "2rem",
                                }}
                                marginRight={1}
                            >
                                Making the process
                            </Typography>
                            <TypeAnimation
                                preRenderFirstString
                                sequence={[
                                    1000,
                                    " better", // initially rendered starting point
                                    750,
                                    " faster",
                                    750,
                                    " more accurate",
                                    750,
                                ]}
                                speed={50}
                                style={{
                                    fontSize: isMob ? "1.5rem" : "2rem",
                                    color: theme.palette.secondary.main,
                                }}
                                repeat={Infinity}
                            />
                        </Box>

                        <Stack direction="column" spacing={3}>
                            <Box display="inline-block">
                                <AppButton
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={navToLogin}
                                >
                                    I&apos;m an installer, let&rsquo;s get
                                    started
                                </AppButton>
                            </Box>
                            <Box display="inline-block">
                                <AppButton
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    endIcon={<ArrowDownwardIcon />}
                                    onClick={(): void =>
                                        scrollToSection(manuRef)
                                    }
                                >
                                    I&apos;m a manufacturer
                                </AppButton>
                                <AppButton
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    endIcon={<ArrowDownwardIcon />}
                                    onClick={(): void =>
                                        scrollToSection(customerRef)
                                    }
                                    sx={{ ml: 2 }}
                                >
                                    I&apos;m a customer
                                </AppButton>
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
                <Grid
                    lg={6}
                    xs={12}
                    alignContent="center"
                    order={{
                        xs: 2,
                        lg: 1,
                    }}
                >
                    <Slide
                        direction="left"
                        in
                        mountOnEnter
                        timeout={SLIDE_IN_TIMEOUT}
                    >
                        <CarSvg width="100%" viewBox="680 340 1100 600" />
                    </Slide>
                </Grid>
                <Grid
                    xs={12}
                    order={{
                        xs: 1,
                        lg: 2,
                    }}
                >
                    <Stack
                        justifyContent="center"
                        direction="row"
                        marginTop={-5}
                    >
                        <IconButton
                            aria-label="Scroll to about section"
                            onClick={(): void => scrollToSection(aboutRef)}
                            sx={{
                                animation: "bounce 2s infinite",
                                "@keyframes bounce": {
                                    "0%": {
                                        transform: "translateY(0)",
                                    },
                                    "50%": {
                                        transform: "translateY(-20px)",
                                    },
                                    "100%": {
                                        transform: "translateY(0)",
                                    },
                                },
                            }}
                        >
                            <KeyboardDoubleArrowDownIcon
                                fontSize="large"
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: "3.2rem",
                                }}
                            />
                        </IconButton>
                    </Stack>
                </Grid>
            </Grid>
            <Box
                width={`calc(100% + ${theme.spacing(
                    getContentFramePadding(isMob) * 2
                )})`}
                bgcolor={theme.palette.primary.main}
                mx={-getContentFramePadding(isMob)} // Parent's padding halfed
                py={SECTION_PY}
                px={2}
                paddingLeft={PADDING_LEFT}
                paddingRight={PADDING_LEFT}
                ref={aboutRef}
            >
                <Typography
                    variant="body1"
                    color={theme.palette.primary.contrastText}
                    textAlign="left"
                    fontWeight={500}
                    style={{
                        textDecoration: "underline",
                        textDecorationThickness: "6px", // Change this to your desired thickness
                        textUnderlineOffset: "10px", // Change this to your desired offset
                    }}
                >
                    Why use ENA Connect Direct?
                </Typography>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={{ xs: 4, sm: 7, md: 10 }}
                    marginTop={MARGIN_SECTION_TITLE_CONTENT}
                >
                    <Box flex={1}>
                        <Typography
                            variant="h4"
                            color={
                                theme.palette.mode === LIGHT
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.contrastText
                            }
                            textAlign="left"
                            fontWeight={500}
                            gutterBottom
                        >
                            better
                        </Typography>
                        <Typography
                            variant="h5"
                            textAlign="left"
                            color={theme.palette.primary.contrastText}
                        >
                            One simple process for all UK domestic low-carbon
                            technology applications, regardless of location or
                            type
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <Typography
                            variant="h4"
                            color={
                                theme.palette.mode === LIGHT
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.contrastText
                            }
                            textAlign="left"
                            fontWeight={500}
                            gutterBottom
                        >
                            faster
                        </Typography>
                        <Typography
                            variant="h5"
                            textAlign="left"
                            color={theme.palette.primary.contrastText}
                        >
                            Connect Direct speeds up the application time and
                            can give you almost instant approval
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <Typography
                            variant="h4"
                            color={
                                theme.palette.mode === LIGHT
                                    ? theme.palette.secondary.main
                                    : theme.palette.primary.contrastText
                            }
                            textAlign="left"
                            fontWeight={500}
                            gutterBottom
                        >
                            more accurate
                        </Typography>
                        <Typography
                            variant="h5"
                            textAlign="left"
                            color={theme.palette.primary.contrastText}
                        >
                            No more paper forms, no more duplicates, thousands
                            of devices to pick from, providing better
                            information to you and the networks
                        </Typography>
                    </Box>
                </Stack>
            </Box>
            <Box
                width={`calc(100% + ${theme.spacing(
                    getContentFramePadding(isMob) * 2
                )})`}
                bgcolor={theme.palette.background.frameBackground}
                mx={-getContentFramePadding(isMob)} // Parent's padding halfed
                py={SECTION_PY}
                px={2}
                paddingLeft={PADDING_LEFT}
                paddingRight={PADDING_LEFT}
            >
                <Typography
                    variant="body1"
                    color={theme.palette.primary.main}
                    textAlign="left"
                    fontWeight={500}
                    style={{
                        textDecoration: "underline",
                        textDecorationThickness: "6px", // Change this to your desired thickness
                        textUnderlineOffset: "10px", // Change this to your desired offset
                    }}
                >
                    For installers
                </Typography>
                <Grid
                    marginTop={MARGIN_SECTION_TITLE_CONTENT}
                    container
                    spacing={2}
                >
                    <Grid xs={12} md={6}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    color="text.primary"
                                    marginBottom={SUBTITLE_MARGIN}
                                >
                                    You get a new streamlined website or API to
                                    submit applications to networks. Free of
                                    charge.
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    component="div"
                                    fontWeight="400"
                                    style={{ lineHeight: "1.4" }}
                                >
                                    As an installer, there are two ways you can
                                    get started...
                                </Typography>
                            </Box>
                            <TabContext value={tabValue}>
                                <TabList
                                    aria-label="Admin Tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    onChange={handleChange}
                                    sx={{
                                        "& .Mui-selected": {
                                            // This targets the selected tab
                                            borderBottom: "20px", // Change this to your desired color and thickness
                                        },
                                    }}
                                >
                                    <AppTab
                                        label={
                                            <Typography variant="h6">
                                                Installers
                                            </Typography>
                                        }
                                        value={
                                            InstallerTabValue.LARGE_INSTALLERS
                                        }
                                    />
                                    <AppTab
                                        label={
                                            <Typography variant="h6">
                                                Software Providers
                                            </Typography>
                                        }
                                        value={
                                            InstallerTabValue.SOFTWARE_PROVIDERS
                                        }
                                    />
                                </TabList>

                                <TabPanel
                                    value={InstallerTabValue.LARGE_INSTALLERS}
                                    sx={{ p: 0 }}
                                >
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                        fontWeight="400"
                                    >
                                        As a solo installer, a member of a team,
                                        or a manager of a team of installers,
                                        you can use ENA Connect Direct to submit
                                        and manage all your domestic LCT
                                        applications from a single place. It is
                                        available nationwide, covering all GB
                                        and NI DNOs and over 90% of iDNOs, and
                                        means you can follow a single process
                                        for every single application:
                                        <CheckMarkList
                                            variant="h6"
                                            fontWeight={400}
                                            secondary
                                            items={[
                                                "Sign-up",
                                                "We will guide you through the steps to set-up your company and make you the admin",
                                                "From there, any team members who sign-up with your company email address will be automatically added to your company",
                                                "You will be able to manage your users, oversee their installations and invite new users to join you",
                                            ]}
                                        />
                                        If you are a member of a company that is
                                        already set-up, simply sign-up with your
                                        company email address and we will add
                                        you to your company automatically.
                                    </Typography>
                                </TabPanel>
                                <TabPanel
                                    value={InstallerTabValue.SOFTWARE_PROVIDERS}
                                    sx={{ p: 0 }}
                                >
                                    <Typography
                                        variant="h6"
                                        color="text.secondary"
                                        fontWeight="400"
                                    >
                                        Connect Direct can be integrated into
                                        your product using our enterprise
                                        connections APIs. To get started:
                                        <CheckMarkList
                                            secondary
                                            variant="h6"
                                            fontWeight={400}
                                            items={[
                                                "Sign-up",
                                                "Pick 'Software Provider' during the set-up process",
                                                "We will give you access to our API documentation",
                                                "We will provide access to our support portal",
                                                "You can then raise a ticket asking for production or sandbox API keys",
                                                "We will create your keys and issue them to you",
                                            ]}
                                        />
                                    </Typography>
                                </TabPanel>
                            </TabContext>
                            <Box display="inline-block">
                                <AppButton
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={navToLogin}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    Get started
                                </AppButton>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            paddingX={3}
                            paddingY={{
                                xs: 3,
                                md: 0,
                            }}
                        >
                            <Box
                                maxWidth="700px"
                                minHeight="400px"
                                sx={{
                                    borderRadius: "40px",
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    backgroundImage: `url(${SolarPanel1})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box
                ref={manuRef}
                width={`calc(100% + ${theme.spacing(
                    getContentFramePadding(isMob) * 2
                )})`}
                bgcolor={theme.palette.background.contentBackground}
                mx={-getContentFramePadding(isMob)} // Parent's padding halfed
                py={SECTION_PY}
                px={2}
                paddingLeft={PADDING_LEFT}
                paddingRight={PADDING_LEFT}
            >
                <Grid container spacing={2}>
                    <Grid
                        xs={12}
                        md={6}
                        order={{
                            xs: 1,
                            md: 1,
                        }}
                    />

                    <Grid
                        xs={12}
                        md={6}
                        order={{
                            xs: 2,
                            md: 2,
                        }}
                    >
                        <Typography
                            variant="body1"
                            color={theme.palette.primary.main}
                            textAlign="left"
                            marginBottom={MARGIN_SECTION_TITLE_CONTENT}
                            fontWeight={500}
                            style={{
                                textDecoration: "underline",
                                textDecorationThickness: "6px", // Change this to your desired thickness
                                textUnderlineOffset: "10px", // Change this to your desired offset
                            }}
                        >
                            For Manufacturers
                        </Typography>
                    </Grid>
                    <Grid
                        xs={12}
                        md={6}
                        order={{
                            xs: 4,
                            md: 3,
                        }}
                    >
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            alignContent="flex-start"
                            paddingX={3}
                            paddingY={{
                                xs: 3,
                                md: 0,
                            }}
                        >
                            <Box
                                maxWidth="700px"
                                minHeight="400px"
                                sx={{
                                    borderRadius: "40px",
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    backgroundImage: `url(${Evcp2})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid
                        xs={12}
                        md={6}
                        order={{
                            xs: 3,
                            md: 4,
                        }}
                    >
                        <Stack spacing={3}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    color="text.primary"
                                    marginBottom={SUBTITLE_MARGIN}
                                >
                                    A new way to manage your low-carbon
                                    technology products
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    component="div"
                                    fontWeight="400"
                                    style={{ lineHeight: "1.4" }}
                                >
                                    Use Connect Direct to register your products
                                    with ENA, get them assessed and make them
                                    available to installers submitting domestic
                                    connection applications. This replaces
                                    existing the Type Test Register and EV
                                    Charger/Heat Pump Spreadsheets. The steps to
                                    get started are simple:
                                    <CheckMarkList
                                        secondary
                                        variant="h6"
                                        fontWeight={400}
                                        items={[
                                            "Sign-up",
                                            "We will guide you through the steps to set-up your company and make you the admin",
                                            "From there, any team members who sign-up with your company email address will be automatically added to your company",
                                            "You will be able to manage your users, oversee their device submissions and invite new users to join you",
                                        ]}
                                    />
                                </Typography>
                            </Box>

                            <Box display="inline-block">
                                <AppButton
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={navToLogin}
                                >
                                    Get started
                                </AppButton>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box
                width={`calc(100% + ${theme.spacing(
                    getContentFramePadding(isMob) * 2
                )})`}
                bgcolor={
                    theme.palette.mode === LIGHT
                        ? theme.palette.primary.light
                        : theme.palette.background.frameBackground
                }
                mx={-getContentFramePadding(isMob)} // Parent's padding halfed
                py={SECTION_PY}
                px={2}
                paddingLeft={PADDING_LEFT}
                paddingRight={PADDING_LEFT}
                ref={customerRef}
            >
                <Typography
                    variant="body1"
                    color={theme.palette.primary.main}
                    textAlign="left"
                    fontWeight={500}
                    style={{
                        textDecoration: "underline",
                        textDecorationThickness: "6px", // Change this to your desired thickness
                        textUnderlineOffset: "10px", // Change this to your desired offset
                    }}
                >
                    For Customers
                </Typography>
                <Grid
                    marginTop={MARGIN_SECTION_TITLE_CONTENT}
                    container
                    spacing={2}
                >
                    <Grid xs={12} md={6}>
                        <Stack spacing={3}>
                            <Typography
                                variant="h4"
                                color="text.primary"
                                marginBottom={SUBTITLE_MARGIN}
                            >
                                A higher quality of service and reduced wait
                                times
                            </Typography>
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                component="div"
                                fontWeight="400"
                                style={{ lineHeight: "1.4" }}
                            >
                                As an customer, whilst you do not directly use
                                Connect Direct, you will benefit from:
                                <CheckMarkList
                                    variant="h6"
                                    fontWeight={400}
                                    secondary
                                    items={[
                                        "Faster connection times",
                                        "A better, safer service from your installer",
                                        "Confidence that network know about and approve your installation",
                                        "Written confirmation of approval to install",
                                    ]}
                                />
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <Box
                            display="flex"
                            width="100%"
                            height="100%"
                            justifyContent="center"
                            paddingX={3}
                            paddingY={{
                                xs: 3,
                                md: 0,
                            }}
                        >
                            <Box
                                maxWidth="700px"
                                minHeight="400px"
                                width="100%"
                                height="100%"
                                sx={{
                                    borderRadius: "40px",
                                    position: "relative",
                                    backgroundImage: `url(${Hp})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box
                width={`calc(100% + ${theme.spacing(
                    getContentFramePadding(isMob) * 2
                )})`}
                bgcolor={
                    theme.palette.mode === LIGHT
                        ? theme.palette.background.frameBackground
                        : theme.palette.background.contentBackground
                }
                mx={-getContentFramePadding(isMob)} // Parent's padding halfed
                py={SECTION_PY}
                px={2}
                paddingLeft={PADDING_LEFT}
                paddingRight={PADDING_LEFT}
            >
                <Typography
                    variant="body1"
                    color={theme.palette.primary.main}
                    textAlign="center"
                    fontWeight={500}
                    style={{
                        textDecoration: "underline",
                        textDecorationThickness: "6px", // Change this to your desired thickness
                        textUnderlineOffset: "10px", // Change this to your desired offset
                    }}
                >
                    Frequently Asked Questions
                </Typography>
                <Box display="flex" justifyContent="center">
                    <Stack
                        width={{
                            xs: "100%",
                            md: "80%",
                            lg: "60%",
                        }}
                        direction="column"
                        spacing={3}
                        marginTop={MARGIN_SECTION_TITLE_CONTENT}
                    >
                        {HOMEPAGE_FAQS.map((faq) => (
                            <Accordion
                                key={faq.question}
                                sx={{
                                    width: "100%",
                                    boxShadow: "none",
                                    background: "transparent",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <KeyboardArrowDownIcon
                                            style={{
                                                fontSize: "40px",
                                            }}
                                        />
                                    }
                                >
                                    <Typography
                                        variant="h5"
                                        paddingTop={1}
                                        fontWeight={500}
                                    >
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography
                                        component="div"
                                        fontWeight={300}
                                    >
                                        {faq.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </>
    )
}

export default PublicHomepage
